/**
 * WHICH BACKEND, decided at RUN time.
 *
 * Read from `/config.json` beside index.html, not from `import.meta.env`. That
 * is the whole portability claim in one decision: build-time env would mean
 * one build artifact per deployment, which is precisely what this work exists
 * to escape. `dist/` is the same bytes on Firebase Hosting, on a VPS and in a
 * PocketBase `pb_public/` - you edit one JSON file.
 *
 * Every field is optional and the defaults are the ones that need no thought:
 *
 *   {}                                        → Firebase (what the live site is)
 *   { "backend": "pocketbase" }               → PocketBase at this same origin
 *   { "backend": "pocketbase",
 *     "pocketbase": { "url": "https://…" } }  → PocketBase somewhere else
 *
 * A same-origin self-host therefore needs NO configuration at all beyond the
 * backend name: PocketBase serves the app out of pb_public, so the API is
 * wherever the page came from.
 */
export interface BackendConfig {
  backend: 'firebase' | 'pocketbase';
  pocketbase?: { url: string };
  /**
   * WHICH SIGN-IN METHODS A FIREBASE DEPLOYMENT OFFERS.
   *
   * PocketBase can be asked (`listAuthMethods`). Firebase cannot - there is no
   * client API for "which providers are enabled", and calling a disabled one
   * fails at the attempt with ADMIN_ONLY_OPERATION. So a Firebase deployment
   * states what it has, and the login screen renders that.
   *
   * Defaults to `['google']`, which is what this app has always offered and
   * what the live project actually has enabled. Adding `'anonymous'` here is
   * the SECOND half of a two-step: it must also be switched on in the Firebase
   * console, or the button appears and then errors - which is precisely the
   * invisible-console-config problem this port exists to reduce.
   */
  firebase?: { methods: ('anonymous' | 'google')[] };
}

const DEFAULT: BackendConfig = { backend: 'firebase' };

/** The origin the page was served from - the right default for a same-origin self-host. */
function sameOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

/**
 * Fetch `/config.json`, falling back to Firebase.
 *
 * A MISSING FILE IS NOT AN ERROR. Firebase Hosting deployments do not need
 * one, and neither does `npm run dev` - the fallback is what the app has
 * always done. A malformed one IS worth complaining about, loudly, because
 * silently running the wrong backend is the worst outcome available here.
 */
export async function loadBackendConfig(fetchImpl: typeof fetch = fetch): Promise<BackendConfig> {
  let raw: unknown;
  try {
    const res = await fetchImpl('/config.json', { cache: 'no-store' });
    if (!res.ok) return DEFAULT;
    raw = await res.json();
  } catch {
    // No file, or not JSON. Both mean "nobody configured this", which is
    // Firebase.
    return DEFAULT;
  }
  return normaliseBackendConfig(raw);
}

/** Exported for tests: the parsing half, with no I/O. */
export function normaliseBackendConfig(raw: unknown): BackendConfig {
  if (!raw || typeof raw !== 'object') return DEFAULT;
  const cfg = raw as Partial<BackendConfig>;

  if (cfg.backend === 'pocketbase') {
    const url = cfg.pocketbase?.url?.trim() || sameOrigin();
    return { backend: 'pocketbase', pocketbase: { url } };
  }
  if (cfg.backend === 'firebase' || !cfg.backend) {
    const methods = cfg.firebase?.methods?.filter((m) => m === 'anonymous' || m === 'google');
    if (methods?.length) return { backend: 'firebase', firebase: { methods } };
    return DEFAULT;
  }
  // Named a backend that does not exist (the two above are the only ones,
  // and an absent name is Firebase). Say so - running Firebase silently
  // would look like the config file was ignored, which is exactly the bug
  // report nobody can act on.
  console.error(`[backend] unknown backend "${cfg.backend}" in /config.json - falling back to firebase`);
  return DEFAULT;
}
